/**
 * SystemBanner — persistent full-width banner for system-level messages.
 * Use for: maintenance windows, degraded service, version updates, CORS issues.
 * Reads state from notificationStore.banner.
 */

import { useNotificationStore } from '../../store/notificationStore';
import {
  StatusInfo as InfoCircleIcon,
  StatusWarning as AlertTriangleIcon,
  StatusDanger as XCircleIcon,
  Close as CloseIcon,
} from '../icons';
import './SystemBanner.css';

const ICON_MAP = {
  info: InfoCircleIcon,
  warning: AlertTriangleIcon,
  error: XCircleIcon,
} as const;

export const SystemBanner = () => {
  const banner = useNotificationStore((s) => s.banner);
  const dismissBanner = useNotificationStore((s) => s.dismissBanner);

  if (!banner) return null;

  const Icon = ICON_MAP[banner.type];

  return (
    <div
      className={`system-banner system-banner--${banner.type}`}
      role={banner.type === 'info' ? 'status' : 'alert'}
      aria-live={banner.type === 'info' ? 'polite' : 'assertive'}
    >
      <Icon className="system-banner__icon" size={16} />
      <span className="system-banner__message">{banner.message}</span>

      {banner.actionLabel && banner.onAction && (
        <button
          className="system-banner__action"
          onClick={banner.onAction}
          type="button"
        >
          {banner.actionLabel}
        </button>
      )}

      {banner.dismissible && (
        <button
          className="system-banner__close"
          onClick={dismissBanner}
          type="button"
          aria-label="Dismiss banner"
        >
          <CloseIcon />
        </button>
      )}
    </div>
  );
};
